import { DownOutlined } from '@ant-design/icons'
import {
  Button,
  Checkbox,
  CheckboxOptionType,
  DatePicker,
  Dropdown,
  Menu,
  Select,
  SelectProps,
  Space,
  Typography,
} from 'antd'
import _ from 'lodash'
import { CSSProperties, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { STATS_PAGE_ALL_PROJECTS } from '../../constants'
import { useStatsAllProjects } from '../../hooks/useStatsAllProjects'
import { dataStore } from '../../store/dataStore'
import { useSiteCustomIds } from '../../store/useSiteCustomIds'
import { AgeGroupEnum, AssignmentFilterEnum, EventType, ProjectWithSites } from '../../Types'

const { RangePicker } = DatePicker
const { Link } = Typography

export function ToggleDbResponders() {
  const statsIncludeDbResponders = dataStore((state) => state.statsIncludeDbResponders)
  const setStatsIncludeDbResponders = dataStore((state) => state.setStatsIncludeDbResponders)

  return (
    <Checkbox checked={statsIncludeDbResponders} onChange={(val) => setStatsIncludeDbResponders(val.target.checked)}>
      Include DB-responders
    </Checkbox>
  )
}

export function FilterAges() {
  const statsAges = dataStore((state) => state.statsAges)
  const setStatsAges = dataStore((state) => state.setStatsAges)

  const ageGroups = Object.values(AgeGroupEnum) as AgeGroupEnum[]

  const options: SelectProps['options'] = ageGroups.map((age) => ({
    label: age,
    value: age,
  }))

  return (
    <Select
      mode="multiple"
      allowClear
      style={{ width: '100%' }}
      placeholder="Age: 1-10, 10-20, ..."
      onChange={(ages: AgeGroupEnum[]) => {
        setStatsAges(ages)
      }}
      value={statsAges}
      options={options}
    />
  )
}

const assignmentFilterLabels: { [filter in AssignmentFilterEnum]: string } = {
  [AssignmentFilterEnum.All]: 'All',
  [AssignmentFilterEnum.OnlyAdminAssigned]: 'Only admin assigned responders',
  [AssignmentFilterEnum.OnlySiteUserAssigned]: 'Only site user assigned responders',
  [AssignmentFilterEnum.ExcludingAssigned]: 'Excluding assigned',
}

export function AssignmentFilter() {
  const statsAssignment = dataStore((state) => state.statsAssignment)
  const setStatsAssignment = dataStore((state) => state.setStatsAssignment)
  const statsIncludeDbResponders = dataStore((state) => state.statsIncludeDbResponders)

  // the filter only applies to db-responders.
  if (!statsIncludeDbResponders) {
    return null
  }

  const options: SelectProps['options'] = (Object.values(AssignmentFilterEnum) as AssignmentFilterEnum[]).map(
    (filter) => ({ label: assignmentFilterLabels[filter], value: filter })
  )

  return (
    <div className="mt-2" style={{ width: '100%' }}>
      <Select
        style={{ width: '100%' }}
        onChange={(filter: AssignmentFilterEnum) => {
          setStatsAssignment(filter)
        }}
        value={statsAssignment}
        options={options}
      />
    </div>
  )
}

export function ExcludeForecastStates() {
  const statsForecastExcludeStates = dataStore((state) => state.statsForecastExcludeStates)
  const setStatsForecastExcludeStates = dataStore((state) => state.setStatsForecastExcludeStates)

  const forecastStates = [
    EventType.ReadyForPhoneScreen,
    EventType.NewReferral,
    EventType.ScheduledForScreening,
    EventType.SignedConsent,
    EventType.ScheduledForRandomisation,
    EventType.SubjectRandomised,
  ] as const

  const options: SelectProps['options'] = forecastStates.map((state) => ({ label: state, value: state }))
  return (
    <Select
      mode="multiple"
      allowClear
      style={{ width: '100%' }}
      placeholder="State1, State2, ..."
      onChange={(states: string[]) => {
        setStatsForecastExcludeStates(states)
      }}
      value={statsForecastExcludeStates}
      options={options}
    />
  )
}

export function ExtraProjectsFilter({ otherProjects }: { otherProjects: ProjectWithSites[] }) {
  const statsExtraProjects = dataStore((state) => state.statsExtraProjects)
  const setStatsExtraProjects = dataStore((state) => state.setStatsExtraProjects)

  const options: SelectProps['options'] = otherProjects.map((project) => ({ label: project.title, value: project._id }))
  return (
    <Select
      mode="multiple"
      allowClear
      style={{ width: '60%' }}
      placeholder="Project1, project2, ..."
      onChange={(ids: string[]) => {
        const projects = ids.map((id) => otherProjects.find((project) => project._id === id)!)
        setStatsExtraProjects(projects)
      }}
      value={statsExtraProjects?.map((project) => project._id)}
      options={options}
    />
  )
}

export function DatesPicker({ style }: { style?: CSSProperties }) {
  const rangeValue = dataStore((state) => state.statsSelectedDateRange)
  const setRangeValue = dataStore((state) => state.setStatsSelectedDateRange)

  return (
    <RangePicker
      value={rangeValue?.rawMomentRangeVal}
      style={style}
      onChange={(val) => {
        if (!val?.[0] && !val?.[1]) {
          // its empty.
          setRangeValue()
          return
        }

        if (!val?.[0] || !val?.[1]) {
          // do nothing if we only have 1 date
          return
        }

        console.log('raw range:', val[0].toISOString(), val[1].toISOString())

        // UTC
        const startDateStr = val[0].toISOString().split('T')[0] + 'T00:00:00.000z'
        const endDateStr = val[1].toISOString().split('T')[0] + 'T23:59:59.000z'

        const start = new Date(startDateStr)
        const end = new Date(endDateStr)

        console.log('Start:', start.toISOString())
        console.log('End:', end.toISOString())

        setRangeValue({
          rawMomentRangeVal: val as [moment.Moment, moment.Moment],
          startDatetime: start.getTime(), //UTC
          endDatetime: end.getTime(), // UTC
        })
      }}
      //example: if a responder is phonescreened we know with 100% certainty that he is also prescreened.
      renderExtraFooter={() => 'Note: deduced events are not included if a specific date range is chosen.'}
    />
  )
}

export function CountryFilter() {
  const { countries } = useStatsAllProjects()
  const setStatsSelectedCountry = dataStore((state) => state.setStatsSelectedCountry)

  const handleChange = (value: string) => {
    console.log(`selected country: ${value}`)
    setStatsSelectedCountry(value)
  }

  // doesn't make sense to include europe as a country.
  const validCountries = countries?.filter((country) => country !== 'Europe')

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        paddingLeft: 30,
      }}
    >
      <div style={{ width: '100%', marginBottom: 10 }}>
        <Select
          defaultValue="all"
          onChange={handleChange}
          style={{ width: 140 }}
          options={[
            {
              value: 'all',
              label: 'All countries',
            },
            {
              value: 'sanos',
              label: 'Sanos',
            },
            ...(validCountries ?? []).map((country) => {
              return {
                value: country,
                label: country,
              }
            }),
          ]}
        />
      </div>
    </div>
  )
}

export function SiteFilter() {
  const { uniqSiteCustomIds, sitesWithoutCustomId } = useSiteCustomIds()

  const checkSiteOptions: CheckboxOptionType[] = uniqSiteCustomIds.map((customId) => {
    //const siteName = site ? site.officialName : 'Unknown site'
    return {
      label: customId,
      value: customId,
    }
  })

  const selectedSiteIds = dataStore((state) => state.statsSelectedSites)
  const setSelectedSites = dataStore((state) => state.setStatsSelectedSites)

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <div style={{ width: '100%', marginBottom: 10 }}>
        <SiteQuickFilters />
      </div>
      <Checkbox.Group
        value={selectedSiteIds ?? []}
        options={checkSiteOptions}
        onChange={(newSelectedSiteCustomIds) => {
          setSelectedSites(newSelectedSiteCustomIds as string[], true)
        }}
      />
      {sitesWithoutCustomId.length > 0 && (
        <p className="text-warning font-small">
          No customId for: {sitesWithoutCustomId.map((s) => s.internalName).join(', ')}
        </p>
      )}
    </div>
  )
}

function SiteQuickFilters() {
  const { uniqSanosCustomIds } = useSiteCustomIds()
  const sites = dataStore((state) => state.sites)
  const setSelectedSites = dataStore((state) => state.setStatsSelectedSites)

  const countriesWithDuplicates = (sites ?? [])
    .map((site) => site.country)
    .filter((country) => country !== undefined)
    .map((c) => String(c))
  const countries: string[] = _.uniq(countriesWithDuplicates)

  const menu = (
    <Menu>
      <Menu.Item
        className="use-pointer"
        onClick={() => {
          const siteIds = (sites ?? []).map((site) => site.customId ?? site._id)
          setSelectedSites(siteIds, true)
        }}
      >
        All
      </Menu.Item>
      <Menu.Item
        className="use-pointer"
        onClick={() => {
          setSelectedSites([], true)
        }}
      >
        None
      </Menu.Item>
      <Menu.Item
        className="use-pointer"
        onClick={() => {
          setSelectedSites(uniqSanosCustomIds, true)
        }}
      >
        Sanos
      </Menu.Item>
      {countries.map((country) => {
        return (
          <Menu.Item
            key={'country' + country}
            className="use-pointer"
            onClick={() => {
              const countrySites = (sites ?? []).filter((site) => site.country === country)
              const customIds = countrySites.map((site) => site.customId ?? site._id)
              const uniqCustomIds = _.uniq(customIds)
              setSelectedSites(uniqCustomIds, true)
            }}
          >
            {country}
          </Menu.Item>
        )
      })}
    </Menu>
  )

  return (
    <Dropdown overlay={menu}>
      <Link href="#" onClick={(e) => e.preventDefault()} style={{ fontSize: '14px', lineHeight: '10px' }}>
        <Space>
          Quick selection
          <DownOutlined style={{ marginTop: -5 }} />
        </Space>
      </Link>
    </Dropdown>
  )
}

export function ApplyFiltersButton() {
  const hasUnAppliedFilters = dataStore((state) => state.hasUnappliedFilters)
  const sethasUnAppliedFilters = dataStore((state) => state.setHasUnappliedFilters)

  const includeDbResponders = dataStore((state) => state.statsIncludeDbResponders)
  const dateRange = dataStore((state) => state.statsSelectedDateRange)
  const selectedSites = dataStore((state) => state.statsSelectedSites)
  const extraProjects = dataStore((state) => state.statsExtraProjects)
  const statsSelectedCountry = dataStore((state) => state.statsSelectedCountry)
  const statsForecastExcludeStates = dataStore((state) => state.statsForecastExcludeStates)
  const statsAges = dataStore((state) => state.statsAges)
  const statsAssignment = dataStore((state) => state.statsAssignment)

  let [, setSearchParams] = useSearchParams()

  useEffect(() => {
    return () => {
      // invalidate futureVisits when filters are unmounted, i.e. changing project.
      dataStore.getState().setFutureVisits(undefined)
    }
  }, [])

  const updateUrlParams = () => {
    const startDate = dateRange?.startDatetime ? new Date(dateRange?.startDatetime!).toISOString() : ''
    const endDate = dateRange?.endDatetime ? new Date(dateRange?.endDatetime!).toISOString() : ''
    const siteIds = selectedSites ? selectedSites.join(';') : ''

    const isAllProjectsStatsPage = window.location.href.includes(STATS_PAGE_ALL_PROJECTS.to)

    if (isAllProjectsStatsPage) {
      setSearchParams({
        startDate,
        endDate,
        country: statsSelectedCountry,
        includeDbResponders: String(includeDbResponders),
      })
    } else {
      // single project stats
      setSearchParams({
        startDate,
        endDate,
        siteIds,
        extraProjectIds: extraProjects?.map((p) => p._id).join(';') ?? '',
        includeDbResponders: String(includeDbResponders),
        forecastExcludeStates: statsForecastExcludeStates?.join(';') ?? '',
        statsAges: statsAges?.join(';') ?? '',
        statsAssignment,
      })
    }
  }

  return (
    <Button
      type={'primary'}
      disabled={!hasUnAppliedFilters}
      onClick={() => {
        updateUrlParams()
        sethasUnAppliedFilters(false)
      }}
    >
      Apply filters
    </Button>
  )
}
